/**
 * Sanitize report form data before sending it to the backend.
 *
 * When an action/finding is no longer marked as already completed, any
 * `closing_comment` / `what_was_done` value that was entered while it was
 * marked as completed should be reset so the backend does not persist stale
 * data. The value is kept while the toggle is off in the UI so the user can
 * toggle it back on without losing their input, but it is stripped at save
 * time.
 *
 * Also removes the deprecated `comments` / `additional_comment` fields that
 * may still exist in documents that have not yet been backfilled.
 */
export function sanitizeReportFormData(
	data: Record<string, any>,
	_reportType: string
): Record<string, any> {
	if (!data || typeof data !== "object") {
		return data;
	}

	const sanitized: Record<string, any> = { ...data };

	const cleanAction = (action: any) => {
		if (!action || typeof action !== "object") return action;
		const isCompleted =
			action.status === "completed" || action.already_completed === true;
		const cleaned: Record<string, any> = { ...action };
		delete cleaned.comments;
		delete cleaned.additional_comment;
		if (!isCompleted) {
			cleaned.closing_comment = null;
		}
		return cleaned;
	};

	const cleanFinding = (finding: any) => {
		if (!finding || typeof finding !== "object") return finding;
		const isCompleted =
			finding.status === "completed" || finding.already_completed === true;
		const cleaned: Record<string, any> = { ...finding };
		delete cleaned.comments;
		delete cleaned.additional_comment;
		if (!isCompleted) {
			// The findings schema stores what_was_done as a string, so reset to an
			// empty string rather than null.
			cleaned.what_was_done = "";
		}
		return cleaned;
	};

	if (Array.isArray(sanitized.follow_up_actions)) {
		sanitized.follow_up_actions = sanitized.follow_up_actions.map(cleanAction);
	}

	if (Array.isArray(sanitized.findings)) {
		sanitized.findings = sanitized.findings.map(cleanFinding);
	}

	return sanitized;
}
